import { useMemo } from 'react';

import { useTheme } from '@mui/material';

import type { ExtendedOptions, ExtendedSeriesType } from '@/components/Chart/types/highcharts';
import getUnitSuffix from '@/components/Chart/utils/getUnitSuffix';
import { CHART_HEIGHT } from '@/features/DataHub/constants/charts';

const usePreviewOptions = (
  baseOptions: ExtendedOptions | undefined,
  textColor?: string,
  backgroundColor?: string
): ExtendedOptions | undefined => {
  const theme = useTheme();

  const resolvedTextColor = textColor || theme.palette.text.primary;
  const resolvedBackgroundColor = backgroundColor || theme.palette.background.paper;
  return useMemo(() => {
    if (!baseOptions) return undefined;

    return {
      ...baseOptions,
      chart: {
        ...baseOptions.chart,
        backgroundColor: resolvedBackgroundColor,
        height: CHART_HEIGHT
      },
      navigator: {
        enabled: false
      },
      rangeSelector: {
        enabled: false
      },
      legend: {
        enabled: true,
        labelFormatter() {
          const custom = (this as unknown as ExtendedSeriesType).userOptions?.custom;
          return custom ? `${this.name}${getUnitSuffix(custom)}` : this.name;
        },
        itemStyle: {
          color: resolvedTextColor,
          fontSize: '0.8em',
          textOverflow: 'ellipsis'
        }
      },
      exporting: {
        ...baseOptions.exporting,
        chartOptions: {
          ...baseOptions.exporting?.chartOptions,
          legend: {
            ...baseOptions.exporting?.chartOptions?.legend,
            labelFormatter() {
              return this.name;
            },
            itemStyle: {
              color: resolvedTextColor,
              fontSize: '14px'
            }
          }
        }
      },
      scrollbar: {
        enabled: false
      }
    };
  }, [baseOptions, resolvedTextColor, resolvedBackgroundColor]);
};

export default usePreviewOptions;
